var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { createRef, PureComponent } from 'react';
import { Sentry, sentrySetFatalScope } from 'app/utils/sentry';
import styles from './messageIframe.module.scss';
import { getNextSiblings, minimizeQuotes, OUTLOOK_QUOTES_STYLES, QUOTE_SELECTORS, wrapElementsInDixaQuote, } from './utils';
function wrapQuoteWithXpath(xpath, document) {
    const xpathResultOutlook = document.evaluate(xpath, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    if (xpathResultOutlook) {
        const parentElm = xpathResultOutlook.parentElement;
        const nextElms = getNextSiblings((parentElm === null || parentElm === void 0 ? void 0 : parentElm.parentElement) === document.body
            ? xpathResultOutlook
            : parentElm);
        wrapElementsInDixaQuote(nextElms);
    }
}
export class MessageIframe extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            iframeHeight: 0,
        };
        this.frameRef = createRef();
        this.mutationObserver = null;
        this._isMounted = true;
        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.resizeFrame = this.resizeFrame.bind(this);
    }
    componentDidMount() {
        var _a;
        const { content, isPlainText } = this.props;
        const iframe = this.getIframeWindow();
        if ((_a = iframe === null || iframe === void 0 ? void 0 : iframe.document) === null || _a === void 0 ? void 0 : _a.body) {
            iframe.document.open();
            iframe.document.close();
            iframe.document.body.style.padding = '0';
            iframe.document.body.style.margin = '0';
            // TODO: [TEAMS-12078] Investigate improper use of innerHTML
            iframe.document.body.innerHTML = isPlainText
                ? `<div style="white-space: pre-wrap">${content}</div>`
                : content;
            // Add base tag to head.
            const base = iframe.document.createElement('base');
            base.setAttribute('target', '_parent');
            iframe.document.getElementsByTagName('head')[0].appendChild(base);
            // Add loader to all images.
            iframe.document.body.querySelectorAll('img').forEach((img) => {
                img.addEventListener('load', this.resizeFrame);
            });
            // Add target _blank to all a tags.
            iframe.document.body.querySelectorAll('a').forEach((a) => {
                a.setAttribute('target', '_blank');
            });
            OUTLOOK_QUOTES_STYLES.map((style) => {
                wrapQuoteWithXpath(`//div[contains(@style, '${style}')]`, iframe.document);
            });
            // Add custom styling and custom script.
            // Styling is for hiding the quotes and script for the toggling logic.
            const style = iframe.document.createElement('style');
            // TODO: [TEAMS-12078] Investigate improper use of innerHTML
            style.innerHTML = `
        ${QUOTE_SELECTORS.join(',')},
        .hidden {
          display: none;
        }
        .show {
          display: block !important;
        }
      `;
            iframe.document.getElementsByTagName('head')[0].appendChild(style);
            minimizeQuotes(iframe.document, iframe.document);
            this.resizeFrame();
            iframe.window.addEventListener('onmouseup', this.handleWindowResize);
            window.addEventListener('resize', this.handleWindowResize);
            this.mutationObserver = new MutationObserver(() => {
                this.resizeFrame();
            });
            if (iframe && iframe.document.body) {
                this.mutationObserver.observe(iframe.document.body, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                });
            }
        }
    }
    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('resize', this.handleWindowResize);
        if (this.mutationObserver)
            this.mutationObserver.disconnect();
        const iframe = this.getIframeWindow();
        if (iframe && iframe.document.body) {
            iframe.window.removeEventListener('onmouseup', this.handleWindowResize);
            iframe.document.body.querySelectorAll('img').forEach((img) => {
                img.removeEventListener('load', this.resizeFrame);
            });
        }
    }
    handleWindowResize() {
        if (!this._isMounted)
            return;
        this.setState({
            iframeHeight: 0,
        });
        this.resizeFrame();
    }
    getIframeWindow() {
        const { current } = this.frameRef;
        return current ? current.contentWindow : null;
    }
    resizeFrame() {
        if (!this._isMounted)
            return;
        const { extraIframeheight } = this.props;
        this.setState({
            iframeHeight: 0,
        }, () => {
            const iframe = this.getIframeWindow();
            if (iframe && iframe.document.body) {
                this.setState({
                    iframeHeight: iframe.document.body.scrollHeight + (extraIframeheight || 0),
                });
            }
        });
    }
    render() {
        const _a = this.props, { className, content, extraIframeheight, isPlainText } = _a, otherProps = __rest(_a, ["className", "content", "extraIframeheight", "isPlainText"]);
        const { iframeHeight } = this.state;
        const rootClass = classnames(className);
        return (_jsx("div", Object.assign({}, otherProps, { className: rootClass }, { children: _jsx("iframe", { title: "message", scrolling: "no", frameBorder: "no", style: { height: `${iframeHeight}px` }, className: styles.container, ref: this.frameRef }) })));
    }
}
export function MessageIframeWithErrorBoundary(props) {
    return (_jsx(Sentry.ErrorBoundary, Object.assign({ beforeCapture: sentrySetFatalScope, fallback: _jsx(Text.Input, Object.assign({ variant: "error" }, { children: "An error occurred and we could not display this message." })) }, { children: _jsx(MessageIframe, Object.assign({}, props)) })));
}
MessageIframe.defaultProps = {
    extraIframeheight: 20,
};
